import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, Plus } from "lucide-react";
import { ScaleDeviceDialog } from "./ScaleDeviceDialog";
import { toast } from "react-toastify";
import { useScaleDeviceStore } from "../../../store/scaleDeviceStore";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types";
import { ScaleDevice } from "../../../types/scaleDevice";

export const ScaleDeviceList = () => {
    const store = useScaleDeviceStore();
    const { createItem, updateItem, deleteItem } = store;
    const { handleApiError } = useApiErrorHandler();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deviceToEdit, setDeviceToEdit] = useState<ScaleDevice | null>(null);

    const handleNew = () => {
        setDeviceToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (device: ScaleDevice) => {
        setDeviceToEdit(device);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (device: ScaleDevice) => {
        const ok = await confirm(`${device.name} terazisini silmek istiyor musun?`);
        if (ok) {
            try {
                await deleteItem(device.id);
                toast.success("Terazi başarıyla silindi");
            } catch (error) {
                handleApiError(error);
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setDeviceToEdit(null);
        store.fetchDataDynamic();
    };

    const handleSaveDevice = async (data: any) => {
        try {
            if (deviceToEdit) {
                await updateItem(deviceToEdit.id, data);
                toast.success(`Terazi Güncellendi: ${data.name}`);
            } else {
                await createItem(data);
                toast.success(`Yeni Terazi Eklendi: ${data.name}`);
            }
            handleCloseModal();
        } catch (error) {
            handleApiError(error);
        }
    };

    // --- Template'ler ---
    const actionBodyTemplate = (device: ScaleDevice) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Düzenle",
                    onClick: () => handleEdit(device)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(device)
                }
            ]}
        />
    );

    const deviceColumns: DataTableColumn<ScaleDevice>[] = [
        { field: "name", header: "Terazi Adı", width: "250px", sortable: true },
        { field: "connectionType", header: "Bağlantı", width: "150px", sortable: true },
        { field: "ipAddress", header: "IP Adresi", width: "150px" },
        { field: "port", header: "Port", width: "100px" },
        { field: "actions", header: "İşlemler", width: "120px", body: actionBodyTemplate, align: "center" }
    ];

    return (
        <div className="w-full h-full p-2 flex flex-col">
            {confirmModal}
            
            <div className="bg-card rounded-xl border border-borderColor shadow-sm overflow-hidden flex-1 flex flex-col">
                <EDataTable<ScaleDevice>
                    tableKey="scale_device_list"
                    useStore={() => store}
                    dataKey="id"
                    title="Terazi Yönetimi"
                    placeholder="Terazi adı ara"
                    searchFields={[{ field: "name", operator: "contains" as SimpleFilter["operator"] }]}
                    actions={
                        <TextButton onClick={handleNew} className="bg-primary text-white hover:bg-primaryHover px-4 py-2 rounded-lg flex items-center gap-2 transition-all">
                            <Plus size={18} /> Yeni Terazi Ekle
                        </TextButton>
                    }
                    columns={deviceColumns}
                />
            </div>

            <ScaleDeviceDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                scaleDevice={deviceToEdit}
                onSave={handleSaveDevice}
            />
        </div>
    );
};
